import { z } from 'zod';
/**
 * Office/HR staff are the only Entra users of this system (ADR-004). Field staff
 * and candidates never authenticate — they reach the portal through a scoped,
 * expiring token instead, so nothing here applies to them.
 *
 * Role names use underscores because Static Web Apps only accepts `[A-Za-z0-9_]`
 * in a role name. `HR.Admin` would be rejected, so the Entra app role *values* use
 * the same underscored spelling and no translation layer is needed anywhere.
 */
export const HR_ROLES = ['HR_Admin', 'HR_Viewer'];
export const hrRoleSchema = z.enum(HR_ROLES);
/**
 * The principal Static Web Apps injects into `x-ms-client-principal` for the API.
 *
 * There is deliberately no `claims` field: the header never carries one. Claims are
 * only available at `/.auth/me` and in the roles-source payload, so modelling them
 * here would invite API code to branch on something it can never receive.
 */
export const clientPrincipalSchema = z.object({
    identityProvider: z.string(),
    userId: z.string(),
    userDetails: z.string(),
    userRoles: z.array(z.string()),
});
export const claimSchema = z.object({
    typ: z.string(),
    val: z.string(),
});
/**
 * What Static Web Apps POSTs to the roles-source function after each successful
 * login. Entra app roles arrive here as claims — they are NOT placed in
 * `userRoles` automatically, which is the whole reason this function exists.
 */
/**
 * Where the roles source lives, in one place. The Functions route is this value; the Static
 * Web Apps `auth.rolesSource` is `/api/` + this value, and the two must never drift — if they
 * do, the platform silently grants nobody a role and every protected route refuses everyone.
 */
export const ROLES_SOURCE_ROUTE = 'roles-source';
/**
 * The envelope Static Web Apps POSTs after a successful login.
 *
 * The authorization decision reads `claims` and nothing else, so every other field is
 * tolerated in whatever shape the platform sends. Requiring them was a real outage: on
 * 2026-09-10 the first sign-in that got far enough to reach this function was refused as an
 * "unexpected payload", granting nobody a role, because the envelope did not match a strict
 * shape the decision never used (docs/mvp/evidence/staging/signin-root-cause-2026-09-10.md).
 *
 * Tolerant here does NOT mean lenient about roles: `claims` entries that are not a
 * well-formed claim are discarded by `trustedClaims`, and `rolesFromClaims` grants only an
 * exact, recognised application role arriving under a trusted claim type.
 */
export const rolesRequestSchema = z.object({
    identityProvider: z.string().nullish(),
    userId: z.string().nullish(),
    userDetails: z.string().nullish(),
    /** Validated per entry, not wholesale, so one odd claim cannot deny every role. */
    claims: z.array(z.unknown()).nullish(),
    accessToken: z.string().nullish(),
});
/** The entries that are genuinely claims. Anything else is dropped, never guessed at. */
export function trustedClaims(raw) {
    if (!Array.isArray(raw))
        return [];
    const claims = [];
    for (const entry of raw) {
        const parsed = claimSchema.safeParse(entry);
        if (parsed.success)
            claims.push(parsed.data);
    }
    return claims;
}
/**
 * What a payload IS, never what it CONTAINS: top-level key names and claim-type names only.
 * A claim value carries identity, `userDetails` is an address and `userId` identifies a
 * person, so none of them may reach a log an operator reads.
 */
export function payloadShape(body) {
    if (body === null || typeof body !== 'object' || Array.isArray(body)) {
        return `keys=(${Array.isArray(body) ? 'array' : typeof body})`;
    }
    const keys = Object.keys(body).sort();
    const claims = trustedClaims(body.claims);
    const raw = body.claims;
    const total = Array.isArray(raw) ? raw.length : 0;
    const types = [...new Set(claims.map((claim) => claim.typ))].sort();
    return `keys=[${keys.join(', ')}] claims=${claims.length}/${total} claimTypes=[${types.join(', ')}]`;
}
export const rolesResponseSchema = z.object({
    roles: z.array(z.string()),
});
/** Claim types Entra uses for app-role assignments, long form and short. */
const ROLE_CLAIM_TYPES = new Set([
    'roles',
    'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
]);
/**
 * Map Entra app-role claims onto our roles.
 *
 * Only the two known roles are ever returned: an unrecognised claim grants nothing,
 * so adding an app role in Entra cannot by itself widen access here.
 */
export function rolesFromClaims(claims) {
    const claimed = new Set(claims.filter((claim) => ROLE_CLAIM_TYPES.has(claim.typ)).map((claim) => claim.val));
    return HR_ROLES.filter((role) => claimed.has(role));
}
/** The signed-in user as the app cares about them. */
export const currentUserSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    roles: z.array(hrRoleSchema),
    isAdmin: z.boolean(),
});
/**
 * What `/api/app/me` answers with — always HTTP 200, carrying the state in the body.
 *
 * The obvious design is 401 when signed out and 403 when signed in without a role. It
 * is wrong here: Static Web Apps rewrites 401 and 403 through `responseOverrides`, and
 * whether that also applies to a linked backend's own responses is undocumented. If it
 * does, the browser receives the SPA's HTML instead of JSON, the client reads that as
 * "signed out", and a user who is signed in but unauthorised is sent back to sign in —
 * a loop that never reaches the no-access screen.
 *
 * Returning 200 with an explicit state removes the dependency on that unknown. Other
 * endpoints keep 401/403; this one reports identity rather than guarding data.
 */
export const authStateSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('signed-out') }),
    z.object({ status: z.literal('no-access'), displayName: z.string() }),
    z.object({ status: z.literal('signed-in'), user: currentUserSchema }),
]);
/** The HR roles carried by a principal, ignoring the built-in SWA roles. */
export function hrRolesOf(principal) {
    return HR_ROLES.filter((role) => principal.userRoles.includes(role));
}
export function toCurrentUser(principal) {
    const roles = hrRolesOf(principal);
    return {
        userId: principal.userId,
        displayName: principal.userDetails,
        roles,
        isAdmin: roles.includes('HR_Admin'),
    };
}
//# sourceMappingURL=auth.js.map